import { useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const DEFAULT_MAX_ATTEMPTS = 10;
const DEFAULT_INITIAL_BACKOFF_MS = 3000;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface UsePollForInboxThreadOptions {
  /** Maximum number of polling attempts before giving up. @default 10 */
  maxAttempts?: number;
  /** Initial backoff delay in milliseconds. Doubles on each subsequent attempt. @default 3000 */
  initialBackoffMs?: number;
  /** Called when polling exhausts all attempts without finding a thread. */
  onMaxAttemptsReached?: () => void;
  /** Called after successfully navigating to the thread. */
  onSuccess?: () => void;
}

/**
 * Returns a function that polls `inbox_threads.load_threads` with the given
 * `mdbFilter` using exponential backoff until a matching thread is found,
 * then navigates to it with `replace: true`.
 */
export const usePollForInboxThread = ({
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  initialBackoffMs = DEFAULT_INITIAL_BACKOFF_MS,
  onMaxAttemptsReached,
  onSuccess,
}: UsePollForInboxThreadOptions = {}) => {
  const queryClient = useTellescopeQueryClient();
  const navigate = useNavigate();
  const { search } = useLocation();

  const pollForThread = useCallback(
    async (mdbFilter: Record<string, unknown>) => {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await wait(initialBackoffMs * Math.pow(2, attempt));

        try {
          const res = await queryClient.fetchInfiniteQuery({
            model: "inbox_threads",
            method: "load_threads",
            request: {
              autobuild: true,
              mdbFilter,
              limit: 1,
            },
            getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
            initialPageParam: undefined,
            paginateRequest: (lastTimestamp, request) => ({
              ...request,
              lastTimestamp,
            }),
          });

          const thread = res.pages.at(0)?.threads.at(0);
          if (thread) {
            navigate(`../${thread.id}${search}`, { replace: true });
            onSuccess?.();
            return;
          }
        } catch {
          // Continue polling on error
        }
      }

      onMaxAttemptsReached?.();
    },
    [initialBackoffMs, maxAttempts, navigate, onMaxAttemptsReached, onSuccess, queryClient, search]
  );

  return pollForThread;
};
